"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, { enumerable: true, get: function() { return m[k]; } });
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.suggestDepositWallet = exports.getRevealedDeposit = exports.revealDeposit = exports.calculateDepositAddress = exports.calculateDepositScriptHash = exports.calculateDepositRefundLocktime = exports.validateDepositScriptParameters = exports.assembleDepositScript = exports.assembleDepositTransaction = exports.submitDepositTransaction = void 0;
const ethers_1 = require("ethers");
const bitcoinjs_lib_1 = require("bitcoinjs-lib");
const bitcoin_1 = require("./bitcoin");
const bitcoin_network_1 = require("./bitcoin-network");
const hex_1 = require("./hex");
const ecpair_1 = require("ecpair");
const tinysecp = __importStar(require("tiny-secp256k1"));
/**
 * Submits a deposit by creating and broadcasting a Bitcoin P2(W)SH
 * deposit transaction.
 * @param deposit - Details of the deposit.
 * @param depositorPrivateKey - Bitcoin private key of the depositor.
 * @param bitcoinClient - Bitcoin client used to interact with the network.
 * @param witness - If true, a witness (P2WSH) transaction will be created.
 *        Otherwise, a legacy P2SH transaction will be made.
 * @param inputUtxos - UTXOs to be used for funding the deposit transaction. So
 *        far only P2WPKH UTXO inputs are supported.
 * @param fee - the value that should be subtracted from the sum of the UTXOs
 *        values and used as the transaction fee.
 * @returns The outcome consisting of:
 *          - the deposit transaction hash,
 *          - the deposit UTXO produced by this transaction.
 * @dev UTXOs are selected for transaction funding based on their types. UTXOs
 *       with unsupported types are skipped. The selection process stops once
 *       the sum of the chosen UTXOs meets the required funding amount.
 *       Be aware that the function will attempt to broadcast the transaction,
 *       although successful broadcast is not guaranteed.
 *  @throws {Error} When the sum of the selected UTXOs is insufficient to cover
 *        the deposit amount and transaction fee.
 */
async function submitDepositTransaction(deposit, depositorPrivateKey, bitcoinClient, witness, inputUtxos, fee) {
    const utxosWithRaw = [];
    for (const utxo of inputUtxos) {
        const utxoRawTransaction = await bitcoinClient.getRawTransaction(utxo.transactionHash);
        utxosWithRaw.push(Object.assign(Object.assign({}, utxo), { transactionHex: utxoRawTransaction.transactionHex }));
    }
    const bitcoinNetwork = await bitcoinClient.getNetwork();
    const { transactionHash, depositUtxo, rawTransaction } = await assembleDepositTransaction(bitcoinNetwork, deposit, depositorPrivateKey, witness, utxosWithRaw, fee);
    // Note that `broadcast` may fail silently (i.e. no error will be returned,
    // even if the transaction is rejected by other nodes and does not enter the
    // mempool, for example due to an UTXO being already spent).
    await bitcoinClient.broadcast(rawTransaction);
    return {
        transactionHash,
        depositUtxo,
    };
}
exports.submitDepositTransaction = submitDepositTransaction;
/**
 * Assembles a Bitcoin P2(W)SH deposit transaction.
 * @param bitcoinNetwork - The target Bitcoin network.
 * @param deposit - Details of the deposit.
 * @param depositorPrivateKey - Bitcoin private key of the depositor.
 * @param witness - If true, a witness (P2WSH) transaction will be created.
 *        Otherwise, a legacy P2SH transaction will be made.
 * @param inputUtxos - UTXOs to be used for funding the deposit transaction. So
 *        far only P2WPKH UTXO inputs are supported.
 * @param fee - Transaction fee to be subtracted from the sum of the UTXOs'
 *        values.
 * @returns The outcome consisting of:
 *          - the deposit transaction hash,
 *          - the deposit UTXO produced by this transaction.
 *          - the deposit transaction in the raw format
 * @dev UTXOs are selected for transaction funding based on their types. UTXOs
 *     with unsupported types are skipped. The selection process stops once
 *     the sum of the chosen UTXOs meets the required funding amount.
 * @throws {Error} When the sum of the selected UTXOs is insufficient to cover
 *        the deposit amount and transaction fee.
 */
async function assembleDepositTransaction(bitcoinNetwork, deposit, depositorPrivateKey, witness, inputUtxos, fee) {
    const network = (0, bitcoin_network_1.toBitcoinJsLibNetwork)(bitcoinNetwork);
    // eslint-disable-next-line new-cap
    const depositorKeyPair = (0, ecpair_1.ECPairFactory)(tinysecp).fromWIF(depositorPrivateKey, network);
    const psbt = new bitcoinjs_lib_1.Psbt({ network });
    psbt.setVersion(1);
    const totalExpenses = deposit.amount.add(fee);
    let totalInputValue = ethers_1.BigNumber.from(0);
    for (const utxo of inputUtxos) {
        const previousOutput = bitcoinjs_lib_1.Transaction.fromHex(utxo.transactionHex).outs[utxo.outputIndex];
        const previousOutputValue = previousOutput.value;
        const previousOutputScript = previousOutput.script;
        // TODO: Add support for other utxo types along with unit tests for the
        //       given type.
        if ((0, bitcoin_1.isP2WPKHScript)(previousOutputScript)) {
            psbt.addInput({
                hash: utxo.transactionHash.reverse().toBuffer(),
                index: utxo.outputIndex,
                witnessUtxo: {
                    script: previousOutputScript,
                    value: previousOutputValue,
                },
            });
            totalInputValue = totalInputValue.add(utxo.value);
            if (totalInputValue.gte(totalExpenses)) {
                break;
            }
        }
        // Skip UTXO if the type is unsupported.
    }
    // Sum of the selected UTXOs must be equal to or greater than the deposit
    // amount plus fee.
    if (totalInputValue.lt(totalExpenses)) {
        throw new Error("Not enough funds in selected UTXOs to fund transaction");
    }
    // Add deposit output.
    psbt.addOutput({
        address: await calculateDepositAddress(deposit, bitcoinNetwork, witness),
        value: deposit.amount.toNumber(),
    });
    // Add change output if needed.
    const changeValue = totalInputValue.sub(totalExpenses);
    if (changeValue.gt(0)) {
        const depositorAddress = (0, bitcoin_1.createAddressFromPublicKey)(hex_1.Hex.from(depositorKeyPair.publicKey), bitcoinNetwork);
        psbt.addOutput({
            address: depositorAddress,
            value: changeValue.toNumber(),
        });
    }
    psbt.signAllInputs(depositorKeyPair);
    psbt.finalizeAllInputs();
    const transaction = psbt.extractTransaction();
    const transactionHash = bitcoin_1.TransactionHash.from(transaction.getId());
    return {
        transactionHash,
        depositUtxo: {
            transactionHash,
            outputIndex: 0,
            value: deposit.amount,
        },
        rawTransaction: {
            transactionHex: transaction.toHex(),
        },
    };
}
exports.assembleDepositTransaction = assembleDepositTransaction;
/**
 * Assembles a Bitcoin locking script for P2(W)SH deposit transaction.
 * @param deposit - Details of the deposit.
 * @returns Script as an un-prefixed hex string.
 */
async function assembleDepositScript(deposit) {
    validateDepositScriptParameters(deposit);
    const chunks = [];
    // All HEXes pushed to the script must be un-prefixed
    chunks.push(Buffer.from(deposit.depositor.identifierHex, "hex"));
    chunks.push(bitcoinjs_lib_1.opcodes.OP_DROP);
    chunks.push(Buffer.from(deposit.blindingFactor, "hex"));
    chunks.push(bitcoinjs_lib_1.opcodes.OP_DROP);
    chunks.push(bitcoinjs_lib_1.opcodes.OP_DUP);
    chunks.push(bitcoinjs_lib_1.opcodes.OP_HASH160);
    chunks.push(Buffer.from(deposit.walletPublicKeyHash, "hex"));
    chunks.push(bitcoinjs_lib_1.opcodes.OP_EQUAL);
    chunks.push(bitcoinjs_lib_1.opcodes.OP_IF);
    chunks.push(bitcoinjs_lib_1.opcodes.OP_CHECKSIG);
    chunks.push(bitcoinjs_lib_1.opcodes.OP_ELSE);
    chunks.push(bitcoinjs_lib_1.opcodes.OP_DUP);
    chunks.push(bitcoinjs_lib_1.opcodes.OP_HASH160);
    chunks.push(Buffer.from(deposit.refundPublicKeyHash, "hex"));
    chunks.push(bitcoinjs_lib_1.opcodes.OP_EQUALVERIFY);
    chunks.push(Buffer.from(deposit.refundLocktime, "hex"));
    chunks.push(bitcoinjs_lib_1.opcodes.OP_CHECKLOCKTIMEVERIFY);
    chunks.push(bitcoinjs_lib_1.opcodes.OP_DROP);
    chunks.push(bitcoinjs_lib_1.opcodes.OP_CHECKSIG);
    chunks.push(bitcoinjs_lib_1.opcodes.OP_ENDIF);
    return bitcoinjs_lib_1.script.compile(chunks).toString("hex");
}
exports.assembleDepositScript = assembleDepositScript;
// eslint-disable-next-line valid-jsdoc
/**
 * Validates the given deposit script parameters. Throws in case of a
 * validation error.
 * @param deposit - The validated deposit script parameters.
 * @dev This function does not validate the depositor's identifier as its
 *      validity is chain-specific. This parameter must be validated outside.
 */
function validateDepositScriptParameters(deposit) {
    if (deposit.blindingFactor.length != 16) {
        throw new Error("Blinding factor must be an 8-byte number");
    }
    if (!(0, bitcoin_1.isPublicKeyHashLength)(deposit.walletPublicKeyHash)) {
        throw new Error("Invalid wallet public key hash");
    }
    if (!(0, bitcoin_1.isPublicKeyHashLength)(deposit.refundPublicKeyHash)) {
        throw new Error("Invalid refund public key hash");
    }
    if (deposit.refundLocktime.length != 8) {
        throw new Error("Refund locktime must be a 4-byte number");
    }
}
exports.validateDepositScriptParameters = validateDepositScriptParameters;
/**
 * Calculates a refund locktime parameter for the given deposit creation timestamp.
 * Throws if the resulting locktime is not a 4-byte number.
 * @param depositCreatedAt - Unix timestamp in seconds determining the moment
 *        of deposit creation.
 * @param depositRefundLocktimeDuration - Deposit refund locktime duration in seconds.
 * @returns A 4-byte little-endian deposit refund locktime as an un-prefixed
 *          hex string.
 */
function calculateDepositRefundLocktime(depositCreatedAt, depositRefundLocktimeDuration) {
    // Locktime is a Unix timestamp in seconds, computed as deposit creation
    // timestamp plus locktime duration.
    const locktime = ethers_1.BigNumber.from(depositCreatedAt + depositRefundLocktimeDuration);
    const locktimeHex = hex_1.Hex.from(locktime.toHexString());
    if (locktimeHex.toString().length != 8) {
        throw new Error("Refund locktime must be a 4 bytes number");
    }
    // Bitcoin locktime is interpreted as little-endian integer so we must
    // adhere to that convention by converting the locktime accordingly.
    return locktimeHex.reverse().toString();
}
exports.calculateDepositRefundLocktime = calculateDepositRefundLocktime;
/**
 * Calculates a Bitcoin locking script hash for P2(W)SH deposit transaction.
 * @param deposit - Details of the deposit.
 * @param witness - If true, a witness script hash will be created.
 *        Otherwise, a legacy script hash will be made.
 * @returns Buffer with script hash.
 */
async function calculateDepositScriptHash(deposit, witness) {
    const script = await assembleDepositScript(deposit);
    // If witness script hash should be produced, SHA256 should be used.
    // Legacy script hash needs HASH160.
    return witness
        ? (0, bitcoin_1.computeSha256)(hex_1.Hex.from(script)).toBuffer()
        : Buffer.from((0, bitcoin_1.computeHash160)(script), "hex");
}
exports.calculateDepositScriptHash = calculateDepositScriptHash;
/**
 * Calculates a Bitcoin target address for P2(W)SH deposit transaction.
 * @param deposit - Details of the deposit.
 * @param network - Network that the address should be created for.
 * @param witness - If true, a witness address will be created.
 *        Otherwise, a legacy address will be made.
 * @returns Address as string.
 */
async function calculateDepositAddress(deposit, network, witness) {
    const scriptHash = await calculateDepositScriptHash(deposit, witness);
    const bitcoinNetwork = (0, bitcoin_network_1.toBitcoinJsLibNetwork)(network);
    if (witness) {
        // OP_0 <hash-length> <hash>
        const p2wshOutput = Buffer.concat([
            Buffer.from([bitcoinjs_lib_1.opcodes.OP_0, 0x20]),
            scriptHash,
        ]);
        return bitcoinjs_lib_1.payments.p2wsh({ output: p2wshOutput, network: bitcoinNetwork })
            .address;
    }
    else {
        // OP_HASH160 <hash-length> <hash> OP_EQUAL
        const p2shOutput = Buffer.concat([
            Buffer.from([bitcoinjs_lib_1.opcodes.OP_HASH160, 0x14]),
            scriptHash,
            Buffer.from([bitcoinjs_lib_1.opcodes.OP_EQUAL]),
        ]);
        return bitcoinjs_lib_1.payments.p2sh({ output: p2shOutput, network: bitcoinNetwork })
            .address;
    }
}
exports.calculateDepositAddress = calculateDepositAddress;
/**
 * Reveals the given deposit to the on-chain Bridge contract.
 * @param utxo - Deposit UTXO of the revealed deposit
 * @param deposit - Data of the revealed deposit
 * @param bitcoinClient - Bitcoin client used to interact with the network
 * @param bridge - Handle to the Bridge on-chain contract
 * @param vault - vault
 * @returns Transaction hash of the reveal deposit transaction as string
 * @dev The caller must ensure that the given deposit data are valid and
 *      the given deposit UTXO actually originates from a deposit transaction
 *      that matches the given deposit data.
 */
async function revealDeposit(utxo, deposit, bitcoinClient, bridge, vault) {
    const depositTx = (0, bitcoin_1.decomposeRawTransaction)(await bitcoinClient.getRawTransaction(utxo.transactionHash));
    return await bridge.revealDeposit(depositTx, utxo.outputIndex, deposit, vault);
}
exports.revealDeposit = revealDeposit;
/**
 * Gets a revealed deposit from the bridge.
 * @param utxo Deposit UTXO of the revealed deposit
 * @param bridge Handle to the Bridge on-chain contract
 * @returns Revealed deposit data.
 */
async function getRevealedDeposit(utxo, bridge) {
    return bridge.deposits(utxo.transactionHash, utxo.outputIndex);
}
exports.getRevealedDeposit = getRevealedDeposit;
/**
 * Suggests a wallet that should be used as the deposit target at the given moment.
 * @param bridge Handle to the Bridge on-chain contract.
 * @returns Compressed (33 bytes long with 02 or 03 prefix) public key of
 *          the wallet.
 */
async function suggestDepositWallet(bridge) {
    return bridge.activeWalletPublicKey();
}
exports.suggestDepositWallet = suggestDepositWallet;
//# sourceMappingURL=deposit.js.map